"use client";

import * as Accordion from "@radix-ui/react-accordion";
import { motion } from "motion/react";
import NextImage from "next/image";

import { Text } from "@/components/ui/text";
import { useAwardsCarouselItems, UseAwardsCarouselItemsProps } from "./use-items";

type AwardsCarouselItemsProps = UseAwardsCarouselItemsProps;

export const AwardsCarouselItems = (props: AwardsCarouselItemsProps) => {
  const { onValueChange, value, list, styles } = useAwardsCarouselItems(props);

  const currentItem = list[Number(value.replace("item-", ""))] ?? null;
  if (!currentItem) return null;

  const MotionImage = motion.create(NextImage);

  return (
    <div className={styles.container()}>
      <div key={currentItem.media.url} className={styles.imageWrapper()}>
        <MotionImage
          src={currentItem.media.url}
          alt={currentItem.media.alternativeText || currentItem.title}
          width={currentItem.media.width}
          height={currentItem.media.height}
          className={styles.image()}
          initial={{ opacity: 0 }}
          animate={{
            opacity: 1,
            x: [0, -6, 6, -6],
            y: [0, -2, 6, -2],
          }}
          transition={{
            opacity: { duration: 0.4 },
            x: {
              duration: 10,
              repeat: Infinity,
              ease: "easeInOut",
            },
            y: {
              duration: 10,
              repeat: Infinity,
              ease: "easeInOut",
            },
          }}
          exit={{ opacity: 0 }}
        />
      </div>
      <Accordion.Root
        type="single"
        value={value}
        onValueChange={onValueChange}
        collapsible={false}
        className={styles.accordionWrapper()}
      >
        {list.map((item, i) => {
          const isOpen = value === `item-${i}`;
          return (
            <Accordion.Item
              key={i}
              value={`item-${i}`}
              className={styles.accordionItem()}
            >
              <Accordion.Header>
                <Accordion.Trigger>
                  <Text
                    tag="span"
                    size="titleLarge"
                    className={styles.title({
                      className: isOpen ? "" : "opacity-25",
                    })}
                  >
                    {item.title}
                  </Text>
                </Accordion.Trigger>
              </Accordion.Header>
              <Accordion.Content forceMount className={styles.accordionContent()}>
                <motion.div
                  initial={false}
                  animate={{
                    opacity: isOpen ? 1 : 0,
                    y: isOpen ? 0 : -8,
                    height: isOpen ? "auto" : 0,
                    visibility: isOpen ? "visible" : "hidden",
                  }}
                  transition={{
                    duration: 0.3,
                    ease: [0.32, 0.72, 0, 1],
                    height: { duration: 0.3 },
                  }}
                >
                  <Text tag="p" size="titleMedium" className={styles.description()}>
                    {item.description}
                  </Text>
                </motion.div>
              </Accordion.Content>
            </Accordion.Item>
          );
        })}
      </Accordion.Root>
    </div>
  );
};
